import "./styles/LiveCallPage.custom.css";

// import react and redux
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CalendarDays, Clock, MapPin, Phone, PhoneCall, SlidersHorizontal, User, X } from "lucide-react";
import { Button, DatePicker, Input, Table } from "antd";
import dayjs from "dayjs";

// import constants and services
import { all_routes } from "../../router/all_routes";
import { acquireCallLock, getCallSocket, isCallLockedByOtherTab } from "./services/callSocket";
import { startNextCallRef } from "./services/callSessionRefs";
import { notify } from "../../../services/toast.config";
import api from "../../../services/api";
import { getChildLabel, INIT_REMINDER, REMINDER_CHIPS } from "./constants/callConstants";

// import components
import CommonSelect from "../../../core/common/commonSelect";
// import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { callActions } from "../../../core/data/redux/live-call/slice";
import type { AppDispatch } from "../../../core/data/redux/store";
import CallHistoryModal from "./components/CallHistoryModal";
import LiveCallFilters from "./components/LiveCallFilters";
import CustomPagination from "./components/CustomPagination";
import UsedCallsTable from "./components/UsedCallsTable";

import { ReminderFilter } from "./types";
import moment from "moment";
import { showBasicAlert } from "../../../core/common/useAlert";
import FreshCallTable from "./components/FreshCallTable";

const { RangePicker } = DatePicker;

const LiveCallPage = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: any) => state.user.user);
  const signature = localStorage.getItem("signature");


  // ── Redux call state ──────────────────────────────────────────────────────
  const call = useSelector((s: any) => s.liveCall);
  const isAutoCallRef = useRef(false);
  const autoCallStoppedRef = useRef(false);

  // Keep refs in sync
  useEffect(() => { isAutoCallRef.current = call.isAutoCall; }, [call.isAutoCall]);
  useEffect(() => { autoCallStoppedRef.current = call.autoCallStopped; }, [call.autoCallStopped]);

  // ── Page-level state ──────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [companyList, setCompanyList] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [isLogModalVisible, setIsLogModalVisible] = useState(false);
  const [visibleLogs, setVisibleLogs] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState({
    companyId: "", companyName: "Select Company", companyType: "",
  });
  const [callType, setCallType] = useState<"Fresh Call" | "Used Calls">("Fresh Call");
  const [filters, setFilters] = useState({
    city: "", district: "", state: "", country: "", type: "",
    start_date: "", end_date: "",
  });
  const [tempFilters, setTempFilters] = useState({
    city: "", district: "", state: "", country: "", type: "",
    start_date: "", end_date: "",
  });

  const [reminderFilter, setReminderFilter] = useState<ReminderFilter>(INIT_REMINDER);
  const [tempReminderFilter, setTempReminderFilter] = useState<ReminderFilter>(INIT_REMINDER);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchQuery, setTempSearchQuery] = useState("");

  // Pagination state for used calls
  const [usedCallPagination, setUsedCallPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });

  // ── Used call filter options from backend ─────────────────────────────────
  const [usedCallFilterOptions, setUsedCallFilterOptions] = useState({
    countries: [] as string[],
    states: [] as string[],
    districts: [] as string[],
    cities: [] as string[],
  });

  const filteredTableDataRef = useRef<any[]>([]);
  const autoCallIndexRef = useRef<number>(0);

  // Declared early so useMemos below can reference it
  const isUsedCall = callType !== "Fresh Call";

  const handleCall = useCallback((record: any) => {
    if (call.activeCall) return;

    if (isCallLockedByOtherTab()) {
      notify.error("A call is already active in another tab. Please end it first.");
      return;
    }

    acquireCallLock(user?.userDetails?.id);

    const rawLogs: any[] = record?.call_logs || [];
    const lastTwo = rawLogs.slice(-2).reverse();

    const groupedMap = new Map<string, any[]>();
    lastTwo.forEach((log: any, i: number) => {
      const dateKey = log.date ? dayjs(log.date).format("YYYY-MM-DD") : "Unknown";
      if (!groupedMap.has(dateKey)) groupedMap.set(dateKey, []);
      groupedMap.get(dateKey)!.push({
        id: i,
        title: getChildLabel(log.type) || log.type || "Call",
        time: log.date,
        description: log.note || undefined,
        reminderDate: log.reminder_date || undefined,
        icon: "ti-phone-call",
        bgColor: "bg-transparent-primary",
      });
    });

    const activities = Array.from(groupedMap.entries())
      .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
      .map(([date, acts]) => ({ date, activities: acts }));

    const callRecord = {
      ...record,
      mobileNumber: record.mobileNumber || null,
      name: record.response?.["Full Name"] || record.name || "Unknown",
      id: record?.id || null,
      activities,
    };

    getCallSocket().emit("startCall", {
      userId: user?.userDetails?.id,
      data: callRecord,
    });
    dispatch(callActions.setActiveCall(callRecord));
    dispatch(callActions.setIsAutoCall(isAutoCallRef.current));
    dispatch(callActions.setSelectedCompany(selectedCompany));
    dispatch(callActions.setIsLeadsMode(false));
  }, [user, dispatch, selectedCompany, call.activeCall]);

  useEffect(() => {
    startNextCallRef.current = () => {
      const data = filteredTableDataRef.current;
      autoCallIndexRef.current += 1;
      const next = data[autoCallIndexRef.current];
      if (next) {
        handleCall(next);
      } else {
        dispatch(callActions.setIsAutoCall(false));
        dispatch(callActions.setAutoCallStopped(true));
        window.dispatchEvent(new CustomEvent('refreshCallsAfterSubmission'));
      }
    };
  }, [handleCall, dispatch]);

  // ── Fetch on callType change ──────────────────────────────────────────────
  useEffect(() => {
    if (!selectedCompany.companyId) return;

    // Clear all filters when switching tabs
    const emptyFilters = { city: "", district: "", state: "", country: "", type: "", start_date: "", end_date: "" };
    setTempFilters(emptyFilters);
    setFilters(emptyFilters);
    setTempReminderFilter(INIT_REMINDER);
    setReminderFilter(INIT_REMINDER);
    setSearchQuery("");
    setTempSearchQuery("");

    if (callType === "Fresh Call") {
      fetchNumbers(selectedCompany);
    } else {
      fetchUsedNumbers(selectedCompany);
      fetchUsedCallFilterOptions(selectedCompany);
    }
  }, [callType]);

  // ── API ───────────────────────────────────────────────────────────────────
  const fetchCompanyList = async () => {
    setIsLoading(true);
    try {
      const response = await api.post("userService/getAllCompanyList", { signature });
      const data = response.data.data.data;
      setCompanyList(data);
      const first = { companyId: data[0].id, companyName: data[0].name, companyType: data[0].type };
      setSelectedCompany(first);
      dispatch(callActions.setSelectedCompany(first));
      fetchNumbers(first);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setIsLoading(false);
    }
  };

  // ── NEW: fetch distinct location values + reminder counts for used calls ──
  const fetchUsedCallFilterOptions = async (company: any) => {
    if (!company.companyId) return;
    try {
      const response = await api.post("callLogsService/get-used-call-filter-options", {
        signature,
        companyId: company.companyId,
        companyType: company.companyType,
      });
      setUsedCallFilterOptions(response.data.data);
    } catch (error) {
      console.error("Error fetching used call filter options:", error);
    }
  };

  // Listen for form submission refresh event
  useEffect(() => {
    const handleRefreshAfterSubmission = () => {
      if (callType === "Fresh Call") {
        fetchNumbers(selectedCompany);
      } else {
        fetchUsedNumbers(
          selectedCompany,
          usedCallPagination.currentPage,
          usedCallPagination.limit,
          filters,
          reminderFilter,
          searchQuery
        );
        fetchUsedCallFilterOptions(selectedCompany);
      }
    };

    window.addEventListener("refreshCallsAfterSubmission", handleRefreshAfterSubmission);
    return () => {
      window.removeEventListener("refreshCallsAfterSubmission", handleRefreshAfterSubmission);
    };
  }, [callType, selectedCompany, usedCallPagination.currentPage, usedCallPagination.limit, filters, reminderFilter, searchQuery]);

  useEffect(() => {
    fetchCompanyList();
  }, []);

  // ── Fresh calls — UNCHANGED ───────────────────────────────────────────────
  const fetchNumbers = async (company: any) => {
    if (!company.companyId) return;
    setIsLoading(true);
    try {
      const response = await api.post("callLogsService/get-call-logs-leads", {
        signature, companyId: company.companyId, companyType: company.companyType,
      });
      setFilteredData(response.data.data);
    } catch (error) {
      console.error("Error fetching call logs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Used calls — now sends all filters to backend ─────────────────────────
  const fetchUsedNumbers = async (
    company: any,
    page: number = 1,
    limit: number = usedCallPagination.limit,
    appliedFilters?: typeof filters,
    appliedReminderFilter?: ReminderFilter,
    appliedSearch?: string,
  ) => {
    if (!company.companyId) return;
    setIsLoading(true);
    const activeFilters = appliedFilters ?? filters;
    const activeReminder = appliedReminderFilter ?? reminderFilter;
    const activeSearch = appliedSearch !== undefined ? appliedSearch : searchQuery;
    try {
      const response = await api.post("callLogsService/get-used-call-logs", {
        signature,
        companyId: company.companyId,
        companyType: company.companyType,
        page,
        limit,
        country: activeFilters.country || undefined,
        state: activeFilters.state || undefined,
        district: activeFilters.district || undefined,
        city: activeFilters.city || undefined,
        type: activeFilters.type || undefined,
        start_date: activeFilters.start_date || undefined,
        end_date: activeFilters.end_date || undefined,
        reminder_preset: activeReminder.preset || undefined,
        reminder_date_from: activeReminder.dateRange?.[0]?.format("YYYY-MM-DD") || undefined,
        reminder_date_to: activeReminder.dateRange?.[1]?.format("YYYY-MM-DD") || undefined,
        search: activeSearch || null,
      });
      setFilteredData(response.data.data.data);
      setUsedCallPagination({
        currentPage: response.data.data.pagination.page,
        totalPages: response.data.data.pagination.totalPages,
        total: response.data.data.pagination.total,
        limit: response.data.data.pagination.limit,
        hasNextPage: response.data.data.pagination.hasNextPage,
        hasPrevPage: response.data.data.pagination.hasPrevPage,
      });
    } catch (error) {
      console.error("Error fetching used call logs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanyChange = (selectedOption: any) => {
    if (!selectedOption) return;
    const newCompany = {
      companyId: selectedOption.value,
      companyName: selectedOption.label.split("-")[0].trim(),
      companyType: selectedOption.label.split("-")[1].trim(),
    };
    setSelectedCompany(newCompany);
    dispatch(callActions.setSelectedCompany(newCompany));
    if (callType === "Fresh Call") {
      fetchNumbers(newCompany);
    } else {
      fetchUsedNumbers(newCompany, 1, usedCallPagination.limit);
      fetchUsedCallFilterOptions(newCompany);
    }
  };

  const handlePageChange = (page: number) => {
    if (callType === "Used Calls") {
      fetchUsedNumbers(selectedCompany, page, usedCallPagination.limit);
    }
  };

  const handleLimitChange = (limit: number) => {
    if (callType === "Used Calls") {
      fetchUsedNumbers(selectedCompany, 1, limit);
    }
  };

  const handleViewLogs = (logs: any[]) => { setVisibleLogs(logs); setIsLogModalVisible(true); };

  const handleApplyFilters = () => {
    if (Object.values(tempFilters).filter(Boolean).length === 0 && tempReminderFilter.preset === "") {
      notify.error("Please select at least one filter before applying.");
      return;
    }
    setFilters(tempFilters);
    setReminderFilter(tempReminderFilter);
    // Used calls: re-fetch page 1 with new filters
    if (isUsedCall) {
      fetchUsedNumbers(selectedCompany, 1, usedCallPagination.limit, tempFilters, tempReminderFilter);
    }
  };

  const handleReset = () => {
    const emptyFilters = { city: "", district: "", state: "", country: "", type: "", start_date: "", end_date: "", };
    setTempFilters(emptyFilters);
    setTempReminderFilter(INIT_REMINDER);
    setFilters(emptyFilters);
    setReminderFilter(INIT_REMINDER);
    // Used calls: re-fetch page 1 with no filters
    if (isUsedCall) {
      fetchUsedNumbers(selectedCompany, 1, usedCallPagination.limit, emptyFilters, INIT_REMINDER);
    }
  };

  const activeFilterCount = useMemo(() => [
    filters.city, filters.district, filters.state, filters.country,
    filters.type, filters.start_date, filters.end_date,
    reminderFilter.preset,
  ].filter(Boolean).length, [filters, reminderFilter]);

  // ── Computed ──────────────────────────────────────────────────────────────

  // uniqueValues: used ONLY for fresh calls (unchanged)
  const uniqueValues = useMemo(() => {
    const getUnique = (key: string) => {
      const seen = new Set<string>();
      return filteredData.map((i: any) => i[key]).filter(Boolean).reduce((acc: string[], val: string) => {
        if (!seen.has(val.trim().toLowerCase())) { seen.add(val.trim().toLowerCase()); acc.push(val.trim()); }
        return acc;
      }, []);
    };
    const getUniqueOutcomes = () => {
      const seen = new Set<string>();
      return filteredData
        .flatMap((r: any) => Array.isArray(r.call_logs) ? r.call_logs.map((l: any) => l?.type).filter(Boolean) : [])
        .reduce((acc: string[], type: string) => {
          if (!seen.has(type.trim().toLowerCase())) { seen.add(type.trim().toLowerCase()); acc.push(type.trim()); }
          return acc;
        }, []);
    };
    return { cities: getUnique("city"), districts: getUnique("district"), states: getUnique("state"), countries: getUnique("country"), outcomes: getUniqueOutcomes() };
  }, [filteredData]);


  const filteredTableData = useMemo(() => {
    if (isUsedCall) return filteredData;
    return filteredData.filter((item: any) => {
      const normalizedMatch = (itemValue: string | any, filterValue: string | any) => {
        if (!itemValue) return false;
        return itemValue?.trim()?.toLowerCase() === filterValue?.trim()?.toLowerCase();
      };

      if (filters.city && !normalizedMatch(item.city, filters.city)) return false;
      if (filters.district && !normalizedMatch(item.district, filters.district)) return false;
      if (filters.state && !normalizedMatch(item.state, filters.state)) return false;
      if (filters.country && !normalizedMatch(item.country, filters.country)) return false;
      if (filters.type && item?.call_logs?.[0]?.type !== filters.type) return false;

      if (filters.start_date) {
        if (dayjs(item.createdAt).isBefore(dayjs(filters.start_date).startOf("day"))) return false;
      }
      if (filters.end_date) {
        if (dayjs(item.createdAt).isAfter(dayjs(filters.end_date).endOf("day"))) return false;
      }

      return true;
    });
  }, [filteredData, filters, isUsedCall]);

  useEffect(() => {
    filteredTableDataRef.current = filteredTableData;
    autoCallIndexRef.current = 0;
  }, [filteredTableData]);

  const baseColumns: any[] = [
    {
      title: "Name", dataIndex: "name",
      render: (text: string) => (
        <div className="d-flex align-items-center gap-2">
          <div className="lc-avatar"><User size={16} /></div>
          <h6 className="lc-name-text">{text}</h6>
        </div>
      ),
      sorter: (a: any, b: any) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Mobile No",
      dataIndex: "mobileNumber",
      render: (text: string) => (
        <p className="lc-text-row">
          <Phone color="#232360" className="me-1" size={14} /> {text || "-"}
        </p>
      ),
    },
    {
      title: "District",
      dataIndex: "district",
      render: (text: string) => (
        <p className="lc-text-row">
          {text ? (<><MapPin color="#232360" className="me-1" size={14} />{text}</>) : "-"}
        </p>
      ),
    },
    {
      title: "City",
      dataIndex: "city",
      render: (text: string) => (
        <p className="lc-text-row">
          {text ? (<><MapPin color="#232360" className="me-1" size={14} />{text}</>) : "-"}
        </p>
      ),
    },
    {
      title: "State",
      dataIndex: "state",
      render: (text: string) => (
        <p className="lc-text-row">
          {text ? (<><MapPin color="#232360" className="me-1" size={14} />{text}</>) : "-"}
        </p>
      ),
    },
    {
      title: "Country",
      dataIndex: "country",
      align: "left",
      render: (text: string) => (
        <p className="lc-text-row">
          {text ? (<><MapPin color="#232360" className="me-1" size={14} />{text}</>) : "-"}
        </p>
      ),
    },
    {
      title: "Actions", dataIndex: "actions",
      align: "center",
      render: (_: any, record: any) => {
        const isCallActive = !!call.activeCall;
        const callBtnTitle = isCallActive
          ? "End current call first"
          : "Call";
        return (
          <div className="d-flex justify-content-center gap-2 align-items-center">
            <button
              type="button"
              disabled={isCallActive}
              onClick={() => {
                if (isCallActive) return;
                isAutoCallRef.current = false;
                dispatch(callActions.setIsAutoCall(false));
                handleCall(record);
              }}
              title={callBtnTitle}
              className="lc-btn-call"
            >
              <i className="ti ti-phone-call me-1" />
            </button>


            <button
              type="button"
              disabled={isCallActive}
              onClick={(e) => {
                if (isCallActive) return;

                e.preventDefault();
                const phone = record?.mobileNumber?.replace(/\D/g, "");
                phone ? window.open(`https://wa.me/${phone}`, "_blank") : showBasicAlert("Phone number not available");
              }}
              title={isCallActive ? "End current call first" : "Whatsapp"}
              className="whatsapp-icon"
            >
              <i className="ti ti-brand-whatsapp" />
            </button>

          </div>
        );
      },
    },
  ];

  const usedColumns: any[] = [
    ...baseColumns,
    {
      title: "Last Call", dataIndex: "last_call",
      align: "center",
      sorter: (a: any, b: any) => {
        const dateA = a?.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b?.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateB - dateA;
      },
      render: (_: any, record: any) =>
        record?.updatedAt
          ? (
            <div>
              <div className="lc-text-row" style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>
                <CalendarDays className="me-1" size={14} /> {moment(record.updatedAt).format("DD MMM YYYY")}
              </div>
              <small className="text-muted">
                <i className="ti ti-clock me-1" style={{ fontSize: 12 }} />{moment(record.updatedAt).format("hh:mm A")}
              </small>
            </div>
          )
          : <span className="text-muted"><Clock className="me-1" size={14} />No calls yet</span>,
    },
    {
      title: "Logs", dataIndex: "call_logs",
      align: "center",
      render: (call_logs: any[]) =>
        Array.isArray(call_logs) && call_logs.length > 0
          ? (
            <Link to="#" onClick={(e) => { e.preventDefault(); handleViewLogs(call_logs); }}>
              <span style={{ fontSize: 12, fontWeight: 600, padding: "6px 10px", borderRadius: 8, background: "#f0f6ff", color: "#1D71E3", border: "1px solid #bfdbfe", cursor: "pointer" }}>
                <i className="ti ti-eye me-1" />View Logs ({call_logs?.length || 0})
              </span>
            </Link>
          )
          : <span style={{ color: "#9ca3af", fontSize: 12 }}>No Logs</span>,
    },
  ];

  const columns = isUsedCall ? usedColumns : baseColumns;

  const options = [
    { key: "Fresh Call", label: "Fresh Call", icon: <PhoneCall size={18} /> },
    { key: "Used Calls", label: "Used Call", icon: <Phone size={18} /> },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="page-wrapper">
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "90vh" }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading Numbers...</p>
          </div>
        </div>
      ) : (
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2" style={{ paddingLeft: 6 }}>
              <h2 className="mb-1">Live Call</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={`${all_routes.liveCall}${location?.search}`}><i className="ti ti-smart-home" /></Link>
                  </li>
                  <li className="breadcrumb-item">CRM</li>
                  <li className="breadcrumb-item active" aria-current="page">Live Call</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="me-3 mb-2">
                <Link
                  to={all_routes.leadsCall + (location?.search || "")}
                  style={{ backgroundColor: "#1D71E3", border: "none", color: "#FFFFFF", padding: "10px 16px", borderRadius: "6px", fontWeight: 500 }}
                >
                  <i className="ti ti-external-link me-1" />Go To Leads Call
                </Link>
              </div>
              <div className="me-3 mb-2">
                <button
                  style={{ border: "1px solid #1D71E3", color: "#1D71E3", padding: "8px 16px", background: "transparent", borderRadius: "6px", fontWeight: 500 }}
                  disabled={filteredTableData.length === 0 || !!call.activeCall}
                  title={call.activeCall ? "End current call before starting auto call" : ""}

                  onClick={() => {
                    if (call.activeCall) return;
                    isAutoCallRef.current = true;
                    autoCallStoppedRef.current = false;
                    autoCallIndexRef.current = 0;
                    dispatch(callActions.setIsAutoCall(true));
                    dispatch(callActions.setAutoCallStopped(false));
                    handleCall(filteredTableData[0]);
                  }}
                >
                  <i className="ti ti-player-play me-1" />Start Auto Call
                </button>
              </div>
              <div className="me-3 mb-2">
                <CommonSelect
                  options={companyList.map((c) => ({ value: c.id, label: `${c.name} - ${c.type}` }))}
                  defaultValue={{ value: selectedCompany.companyId, label: `${selectedCompany.companyName} - ${selectedCompany.companyType}` }}
                  onChange={handleCompanyChange}
                  isDisabled={!!call.isAutoCall && !!call.activeCall}
                />
              </div>
              <div className="ms-2 head-icons"><CollapseHeader /></div>
            </div>
          </div>

          <div className="card" style={{ overflow: "hidden", gap: 6, backgroundColor: "transparent", border: "none", boxShadow: "none" }}>
            <div className="slide-in-left lc-header-container">
              <div className="lc-header-inner">

                {/* LEFT SIDE */}
                <div className="lc-left-actions">
                  {options.map((item) => {
                    const isActive = callType === item.key;
                    const isDisabled = ((!!call.isAutoCall && !!call.activeCall) && !isActive);
                    return (
                      <button
                        key={item.key}
                        onClick={() => !isDisabled && setCallType(item.key as any)}
                        disabled={isDisabled}
                        title={isDisabled && !!call.activeCall ? "End current call before switching to used calls" : ""}
                        className={`lc-pill-btn ${isActive ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
                      >
                        {item.icon}
                        {item.label}
                      </button>
                    );
                  })}
                </div>

                {/* RIGHT SIDE */}
                <div className="lc-right-actions">
                  {/* Reminder Chips — only for used calls */}
                  {isUsedCall && (
                    <div className="lc-reminder-group">
                      <div className="d-flex gap-2 align-items-center">
                        <Input
                          placeholder="Search by name, number, state, city..."
                          value={tempSearchQuery}                        // ← ADD value
                          onChange={(e) => setTempSearchQuery(e.target.value)}  // ← ADD onChange
                          onPressEnter={() => {                          // ← ADD onPressEnter (optional UX)
                            setSearchQuery(tempSearchQuery);
                            fetchUsedNumbers(selectedCompany, 1, usedCallPagination.limit, filters, reminderFilter, tempSearchQuery);
                          }}
                          style={{ width: 210 }}
                          allowClear
                          onClear={() => {
                            setTempSearchQuery("");
                            setSearchQuery("");
                            fetchUsedNumbers(selectedCompany, 1, usedCallPagination.limit, filters, reminderFilter, "");
                          }}
                          className="search-btn"
                        />
                        <Button
                          className="search-btn"
                          onClick={() => {
                            setSearchQuery(tempSearchQuery);
                            fetchUsedNumbers(selectedCompany, 1, usedCallPagination.limit, filters, reminderFilter, tempSearchQuery);
                          }}
                        >
                          <i className="ti ti-search me-1" />
                        </Button>
                      </div>


                      {REMINDER_CHIPS.map((chip) => {
                        const isActive = reminderFilter.preset === chip.value;

                        return (
                          <button
                            key={chip.value}
                            onClick={() => {
                              const newReminder = { preset: chip.value, dateRange: null };
                              setReminderFilter(newReminder);
                              setTempReminderFilter(newReminder);
                              // Re-fetch used calls with new reminder preset only if it is not custom
                              if (chip.value !== "custom") {
                                fetchUsedNumbers(selectedCompany, 1, usedCallPagination.limit, filters, newReminder);
                              }
                            }}
                            className={`lc-reminder-chip ${isActive ? "active" : ""}`}
                            style={
                              isActive
                                ? { borderColor: chip.activeBorder, background: chip.activeBg, color: chip.activeColor, boxShadow: `0 0 0 2px ${chip.activeBorder}30` }
                                : undefined
                            }
                          >
                            <i className={`ti ${chip.icon}`} />
                            {chip.label}
                          </button>
                        );
                      })}

                      {reminderFilter.preset === "custom" && (
                        <RangePicker
                          size="small"
                          format="DD MMM YYYY"
                          placeholder={["From date", "To date"]}
                          className="lc-range-picker"
                          value={reminderFilter.dateRange}
                          onChange={(dates) => {
                            const newReminder = { preset: "custom" as any, dateRange: dates as any };
                            setReminderFilter(newReminder);
                            setTempReminderFilter(newReminder);
                            // Re-fetch only when both dates are selected
                            if (dates?.[0] && dates?.[1]) {
                              fetchUsedNumbers(selectedCompany, 1, usedCallPagination.limit, filters, newReminder);
                            }
                          }}
                        />
                      )}
                    </div>
                  )}

                  {/* Filter Toggle */}
                  <button onClick={() => setShowFilters((v) => !v)} className="lc-filter-toggle">
                    <SlidersHorizontal size={16} />
                    {showFilters ? "Hide Filters" : "Show Filters"}
                  </button>

                  {/* Clear Filter Button - Only show when there are active filters */}
                  {activeFilterCount > 0 && (
                    <button
                      onClick={handleReset}
                      className="lc-filter-toggle lc-clear-filter-active"
                      title="Clear all filters"
                    >
                      <X size={16} />
                      Clear ({activeFilterCount})
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Below tabs: filter and table in a row */}
            <div className="livecall-layout align-top">
              {/* Table Section */}
              <div className="livecall-table-section">
                {selectedCompany.companyId && (
                  <div className="card-body p-0 slide-up-enter lc-table-card">
                    {filteredTableData.length > 0 ? (
                      callType === "Used Calls" ? (
                        <UsedCallsTable
                          dataSource={filteredTableData}
                          columns={columns}
                          loading={isLoading}
                        />
                      ) : (
                        <div className="card">
                          <FreshCallTable
                            data={filteredTableData}
                            columns={columns}
                            tableDataRef={filteredTableDataRef}
                            indexRef={autoCallIndexRef}
                          />
                        </div>

                      )
                    ) : (
                      <div className="d-flex justify-content-center align-items-center p-5">
                        <p className="text-muted">No numbers match current filters</p>
                      </div>
                    )}
                    {callType === "Used Calls" && (
                      <CustomPagination
                        currentPage={usedCallPagination.currentPage}
                        totalPages={usedCallPagination.totalPages}
                        total={usedCallPagination.total}
                        limit={usedCallPagination.limit}
                        hasNextPage={usedCallPagination.hasNextPage}
                        hasPrevPage={usedCallPagination.hasPrevPage}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Filter Section */}
              <div className={`slide-in-left livecall-filters-section${showFilters ? "" : " hidden"}`}>
                <LiveCallFilters
                  showFilters={showFilters}
                  tempFilters={tempFilters}
                  // Used calls: pass backend options; fresh calls: pass computed uniqueValues
                  uniqueValues={
                    isUsedCall
                      ? {
                        countries: usedCallFilterOptions.countries,
                        states: usedCallFilterOptions.states,
                        districts: usedCallFilterOptions.districts,
                        cities: usedCallFilterOptions.cities,
                        outcomes: [], // not used — component uses static REFERENCE_OPTIONS
                      }
                      : uniqueValues
                  }
                  isUsedCall={isUsedCall}
                  activeFilterCount={activeFilterCount}
                  setTempFilters={setTempFilters}
                  handleApplyFilters={handleApplyFilters}
                  handleReset={handleReset}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">2025 © CMS.</p>
        <p>Designed & Developed By <Link to="#" className="text-primary" onClick={(e: any) => e.preventDefault()}>ZYVO</Link></p>
      </div>

      <CallHistoryModal open={isLogModalVisible} logs={visibleLogs} onClose={() => setIsLogModalVisible(false)} />
    </div>
  );
};

export default LiveCallPage;
